// src/features/auth/state/ui-state/uiSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';

// Types
type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
  timestamp: string;
}

interface Modal {
  id: string;
  component: string;
  props?: Record<string, unknown>;
}

interface LoadingState {
  global: boolean;
  [key: string]: boolean; // Allow specific loading states
}

interface UIState {
  loading: LoadingState;
  notifications: Notification[];
  modals: Modal[];
  errors: Record<string, string>;
  sidebarOpen: boolean;
  theme: 'light' | 'dark';
  redirectUrl: string | null;
}

// Initial state
const initialState: UIState = {
  loading: {
    global: false,
  },
  notifications: [],
  modals: [],
  errors: {},
  sidebarOpen: false,
  theme: 'light',
  redirectUrl: null,
};

// Slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Loading actions
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.global = action.payload;
    },
    setLoading: (state, action: PayloadAction<{ key: string; value: boolean }>) => {
      state.loading[action.payload.key] = action.payload.value;
    },
    clearLoading: (state, action: PayloadAction<string>) => {
      delete state.loading[action.payload];
    },

    // Notification actions
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: `notification-${Date.now()}-${Math.random()}`,
        timestamp: new Date().toISOString(),
        duration: action.payload.duration || 5000,
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },

    // Modal actions
    openModal: (state, action: PayloadAction<{ component: string; props?: Record<string, unknown> }>) => {
      const modal: Modal = {
        id: `modal-${Date.now()}`,
        component: action.payload.component,
        props: action.payload.props,
      };
      state.modals.push(modal);
    },
    closeModal: (state, action: PayloadAction<string>) => {
      state.modals = state.modals.filter(m => m.id !== action.payload);
    },
    closeAllModals: (state) => {
      state.modals = [];
    },

    // Error actions
    setError: (state, action: PayloadAction<{ key: string; message: string }>) => {
      state.errors[action.payload.key] = action.payload.message;
    },
    clearError: (state, action: PayloadAction<string>) => {
      delete state.errors[action.payload];
    },
    clearAllErrors: (state) => {
      state.errors = {};
    },

    // UI state actions
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
    },
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
    },
    setRedirectUrl: (state, action: PayloadAction<string | null>) => {
      state.redirectUrl = action.payload;
    },
  },
});

// Actions
export const {
  setGlobalLoading,
  setLoading,
  clearLoading,
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  closeAllModals,
  setError,
  clearError,
  clearAllErrors,
  toggleSidebar,
  setSidebarOpen,
  setTheme,
  toggleTheme,
  setRedirectUrl,
} = uiSlice.actions;

// Helper action creators
export const showNotification = (
  type: NotificationType,
  title: string,
  message?: string,
  duration?: number
) => addNotification({ type, title, message, duration });

export const showSuccess = (title: string, message?: string) =>
  showNotification('success', title, message);

export const showError = (title: string, message?: string) =>
  showNotification('error', title, message);

export const showWarning = (title: string, message?: string) =>
  showNotification('warning', title, message);

export const showInfo = (title: string, message?: string) =>
  showNotification('info', title, message);

// Selectors
export const selectUI = (state: RootState) => state.ui;
export const selectGlobalLoading = (state: RootState) => state.ui.loading.global;
export const selectLoading = (key: string) => (state: RootState) => state.ui.loading[key] || false;
export const selectNotifications = (state: RootState) => state.ui.notifications;
export const selectModals = (state: RootState) => state.ui.modals;
export const selectErrors = (state: RootState) => state.ui.errors;
export const selectError = (key: string) => (state: RootState) => state.ui.errors[key];
export const selectSidebarOpen = (state: RootState) => state.ui.sidebarOpen;
export const selectTheme = (state: RootState) => state.ui.theme;
export const selectRedirectUrl = (state: RootState) => state.ui.redirectUrl;

// Export reducer
export const uiReducer = uiSlice.reducer;
export default uiSlice.reducer;


